import { alpha, Box, Grid, Stack, styled, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import ErrorBoundary from '../../Common/_components/ErrorBoundary';
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import ProductCatalogCardImage from '../../ProductCatalog/Card/ProductCatalogCardImage';
import { EditPaginationLoader, FetchPaginationItems } from '../../UserData/_actions/UserDataActions';

const RECENTLY_DESIGNED_LIST_ID = 'design-lab-recently-designed-products';
const RECENT_PRODUCT_DESIGN_FETCH_LIMIT = 48;
const MAX_RECENTLY_DESIGNED_PRODUCTS_SHOWN = 6;

const RecentlyDesignedSection = styled(Box)(({ theme }) => ({
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  paddingBottom: theme.spacing(3),

  [theme.breakpoints.down('sm')]: {
    paddingBottom: theme.spacing(2),
  },
}));

const RecentlyDesignedHeader = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const RecentlyDesignedProductsList = styled(Stack)(({ theme }) => ({
  flexWrap: 'nowrap',

  [theme.breakpoints.down('xl')]: {
    '& > :nth-of-type(n + 6)': {
      display: 'none',
    },
    '& > :nth-of-type(n + 5)': {
      display: 'none',
    },
  },

  [theme.breakpoints.down('lg')]: {
    '& > :nth-of-type(n + 3)': {
      display: 'none',
    },
  },

  [theme.breakpoints.down('md')]: {
    '& > :nth-of-type(n + 4)': {
      display: 'flex',
    },
  },

  [theme.breakpoints.down('sm')]: {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(-1),
    scrollSnapType: 'x proximity',
    scrollbarWidth: 'thin',

    '& > :nth-of-type(n + 3)': {
      display: 'flex',
    },
  },
}));

const RecentlyDesignedProductButton = styled('button', {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected?: boolean }>(({ selected, theme }) => ({
  alignItems: 'center',
  appearance: 'none',
  background: '#fff',
  border: '1px solid ' + (selected === true ? 'transparent' : 'rgba(0, 0, 0, 0.08)'),
  borderRadius: 8,
  color: theme.palette.text.primary,
  cursor: 'pointer',
  display: 'flex',
  flex: '1 1 190px',
  font: 'inherit',
  maxWidth: 300,
  minHeight: 72,
  minWidth: 0,
  overflow: 'visible',
  padding: 0,
  position: 'relative',
  textAlign: 'left',
  transition: 'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',

  '&:hover': {
    borderColor: selected === true ? 'transparent' : alpha(theme.palette.accent.main, 0.36),
    boxShadow: '0 8px 18px rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-1px)',
  },

  '&:focus-visible': {
    borderColor: selected === true ? 'transparent' : alpha(theme.palette.accent.main, 0.5),
    outline: '2px solid ' + alpha(theme.palette.accent.main, 0.22),
    outlineOffset: 2,
  },

  '&::after': {
    border: '2px solid #000',
    borderRadius: 'inherit',
    bottom: -1,
    boxSizing: 'border-box',
    content: '""',
    left: -1,
    opacity: selected === true ? 1 : 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: -1,
    top: -1,
    zIndex: 2,
    transition: 'opacity 180ms ease',
  },

  [theme.breakpoints.down('sm')]: {
    flex: '0 0 min(280px, calc(100vw - 32px))',
    maxWidth: 'none',
    scrollSnapAlign: 'start',
  },
}));

const RecentlyDesignedImage = styled('div')(({ theme }) => ({
  background: alpha(theme.palette.secondary.main, 0.06),
  flexShrink: 0,
  height: 72,
  overflow: 'hidden',
  position: 'relative',
  width: 72,
  borderRadius: '8px 0 0 8px',

  'img': {
    display: 'block',
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
}));

const RecentlyDesignedProductName = styled(Typography)({
  display: '-webkit-box',
  lineClamp: 2,
  lineHeight: '1.3',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
});

interface RecentlyDesignedProductsProps {
  enabled: boolean
  maxProductsShown?: number
  onOpenProduct: (viewProduct: ViewProductStore) => void
  onSelectProduct: (viewProduct: ViewProductStore) => void
  selectedViewProductId?: number
}

function RecentlyDesignedProducts({
  enabled,
  maxProductsShown,
  onOpenProduct,
  onSelectProduct,
  selectedViewProductId,
}: RecentlyDesignedProductsProps) {
  const dispatch = useAppDispatch();
  const [t] = useTranslation('design-lab');
  const products = useAppSelector(state => state.get('appData').get('products'));
  const viewProducts = useAppSelector(state => state.get('appData').get('viewProducts'));
  const productDesigns = useAppSelector(state => state.get('userData').get('productDesigns').get('data'));
  const productDesignItemList = useAppSelector(state => state.get('userData')
    .get('productDesigns')
    .get('itemLists')
    .get(RECENTLY_DESIGNED_LIST_ID));

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const data = new FormData();
    data.append('offset', '0');
    data.append('limit', String(RECENT_PRODUCT_DESIGN_FETCH_LIMIT));
    data.append('sort', 'n-to-o');

    dispatch(EditPaginationLoader('productDesigns', RECENTLY_DESIGNED_LIST_ID, true));
    dispatch(FetchPaginationItems(RECENTLY_DESIGNED_LIST_ID, data, 'productDesigns').set({
      onFailure: (_response, dispatch) => {
        dispatch(EditPaginationLoader('productDesigns', RECENTLY_DESIGNED_LIST_ID, false));
      },
      onSuccess: (_response, dispatch) => {
        dispatch(EditPaginationLoader('productDesigns', RECENTLY_DESIGNED_LIST_ID, false));
      },
    }));
  }, [dispatch, enabled]);

  const recentlyDesignedProducts = useMemo(() => {
    const recentProducts: ViewProductStore[] = [];
    const recentViewProductIds = new Set<number>();

    productDesignItemList?.get('items').forEach(productDesignId => {
      const productDesign = productDesigns.get(productDesignId);
      if (productDesign === undefined) {
        return;
      }

      const savedViewProductId = productDesign.get('id_view_product');
      const savedViewProduct = savedViewProductId !== null ? viewProducts.get(String(savedViewProductId)) : undefined;
      const savedViewProductProductId = savedViewProduct?.get('id_product');

      if (
        savedViewProduct !== undefined
        && savedViewProductProductId !== undefined
        && savedViewProductProductId !== null
        && products.get(String(savedViewProductProductId))?.get('visible')
      ) {
        if (!recentViewProductIds.has(savedViewProduct.get('id'))) {
          recentViewProductIds.add(savedViewProduct.get('id'));
          recentProducts.push(savedViewProduct);
        }
      }
    });

    return recentProducts.slice(0, maxProductsShown ?? MAX_RECENTLY_DESIGNED_PRODUCTS_SHOWN);
  }, [maxProductsShown, productDesignItemList, productDesigns, products, viewProducts]);

  const renderRecentlyDesignedProduct = useCallback((product: ViewProductStore) => (
    <RecentlyDesignedProductButton
      key={product.get('id')}
      aria-pressed={selectedViewProductId === product.get('id')}
      selected={selectedViewProductId === product.get('id')}
      type="button"
      onClick={() => onSelectProduct(product)}
      onDoubleClick={() => onOpenProduct(product)}
    >
      <RecentlyDesignedImage>
        <ErrorBoundary fallback={<></>}>
          <ProductCatalogCardImage
            slug={product.get('slug')}
            name={product.get('name')}
            sizes="72px"
          />
        </ErrorBoundary>
      </RecentlyDesignedImage>
      <Stack spacing={0.25} sx={{ justifyContent: 'center', minWidth: 0, px: 1 }}>
        <RecentlyDesignedProductName variant="h4">{product.get('name')}</RecentlyDesignedProductName>
      </Stack>
    </RecentlyDesignedProductButton>
  ), [onOpenProduct, onSelectProduct, selectedViewProductId]);

  if (recentlyDesignedProducts.length === 0) return null;

  return <Grid item xs={12}>
    <RecentlyDesignedSection component="section">
      <RecentlyDesignedHeader>
        <Typography variant="h4">{t('Recently designed')}</Typography>
      </RecentlyDesignedHeader>
      <RecentlyDesignedProductsList direction="row" spacing={1}>
        {recentlyDesignedProducts.map(renderRecentlyDesignedProduct)}
      </RecentlyDesignedProductsList>
    </RecentlyDesignedSection>
  </Grid>
}

export default memo(RecentlyDesignedProducts);
